import type { GuideFixedSlot, GuideMedia, GuideTipKind } from '../../../../interfaces/stays-guide';
import { Clock, resolveClock } from '../../../../function-utils/building-time';

/**
 * THE HOST GUIDE EDITOR'S WORDS AND ARITHMETIC (plan S7.8). Pure: no React, no
 * request. Copy marked FIXED in ai-plans/stays/reports/PLAN-S7-FE-CONTRACT.md
 * §4 is read verbatim by the journey and the matrix — keep it exact.
 */

/** FIXED — every write control without stays:manage. */
export const READ_ONLY_REASON = 'You can view the guide. Editing needs Stays management access.';

/** FIXED — the 422 CODE_IN_TEXT field error. */
export const CODE_IN_TEXT_COPY = 'This text contains a door code. Keep codes in Access details only.';

/** FIXED — the 409 STALE_GUIDE banner. */
export const STALE_GUIDE_COPY = 'Someone saved this guide a moment ago. Reload to see their changes.';

export const FIXED_SLOTS: ReadonlyArray<GuideFixedSlot> = [
  'cover',
  'host',
  'step1',
  'step2',
  'step3',
  'step4',
  'step5',
  'step6',
];

/** FIXED slot names: "Cover", "Host photo", "Step 01" … "Step 06". */
export const slotLabel = (slot: GuideFixedSlot): string =>
  slot === 'cover' ? 'Cover' : slot === 'host' ? 'Host photo' : `Step 0${slot.slice(4)}`;

/** Steps 3–6 are released with the door codes. */
export const isAfterCheckinSlot = (slot: GuideFixedSlot): boolean =>
  slot === 'step3' || slot === 'step4' || slot === 'step5' || slot === 'step6';

export const TIP_KINDS: ReadonlyArray<{ value: GuideTipKind; label: string }> = [
  { value: 'eat', label: 'Eat' },
  { value: 'see', label: 'See' },
  { value: 'nearby', label: 'Nearby' },
  { value: 'transport', label: 'Transport' },
  { value: 'other', label: 'Other' },
];

export const tipKindLabel = (kind: GuideTipKind): string =>
  (TIP_KINDS.find(k => k.value === kind) || { label: kind }).label;

/** The gallery, in its order. */
export const galleryOf = (media: GuideMedia[]): GuideMedia[] =>
  media.filter(m => m.slot === 'gallery').sort((a, b) => a.sort_order - b.sort_order);

/** A photo's name in a control: its title, else "photo <n>" (1-based). */
export const photoName = (m: GuideMedia, index: number): string => (m.title && m.title.trim()) || `photo ${index + 1}`;

/**
 * A listing's wall time ("15:00") moved by `minutes`, as a wall time and the
 * number of days it crossed (-1 = the day before). String arithmetic only —
 * the listing's check-in time is a building wall time, never a JS Date.
 */
export const shiftWallTime = (hhmm: string, minutes: number): { time: string; days: number } | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return null;
  const total = Number(m[1]) * 60 + Number(m[2]) + minutes;
  const days = Math.floor(total / 1440);
  const inDay = ((total % 1440) + 1440) % 1440;
  const pad = (n: number) => String(n).padStart(2, '0');

  return { time: `${pad(Math.floor(inDay / 60))}:${pad(inDay % 60)}`, days };
};

const dayWords = (days: number): string =>
  days === 0 ? '' : days === -1 ? ' the day before' : days === 1 ? ' the day after' : days < 0 ? ` ${-days} days before` : ` ${days} days after`;

const hhmm = (t: string): string => {
  const s = shiftWallTime(t, 0);

  return s ? s.time : t;
};

/** FIXED: "With check-in at 15:00, codes appear at 14:30." */
export const revealHint = (checkIn: string, revealMinutes: number): string => {
  const at = shiftWallTime(checkIn, -revealMinutes);
  if (!at) return `Door codes appear ${revealMinutes} minutes before check-in.`;

  return `With check-in at ${hhmm(checkIn)}, codes appear at ${at.time}${dayWords(at.days)}.`;
};

export const leadHint = (leadHours: number): string =>
  `Guests can open the guide ${leadHours} ${leadHours === 1 ? 'hour' : 'hours'} before check-in.`;

export const expiryHint = (checkOut: string, expiryMinutes: number): string => {
  const at = shiftWallTime(checkOut, expiryMinutes);
  if (!at) return `Access ends ${expiryMinutes} minutes after check-out.`;

  return `With check-out at ${hhmm(checkOut)}, access ends at ${at.time}${dayWords(at.days)}.`;
};

/** FIXED: "Shown to completed guests from 30 minutes before check-in until 30 minutes after check-out." */
export const accessWindowHelper = (reveal: number, expiry: number): string =>
  `Shown to completed guests from ${reveal} minutes before check-in until ${expiry} minutes after check-out.`;

/** The host's own clock (R-T12: owners and managers read their device's). */
export const deviceClock = (): Clock => resolveClock({ surface: 'console' });

export const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

/** True when an instant is more than 90 days ago (the stale-codes pill). */
export const olderThan90Days = (iso: string | null | undefined, now: number = Date.now()): boolean => {
  if (!iso) return false;
  const at = Date.parse(iso);

  return Number.isFinite(at) && now - at > NINETY_DAYS_MS;
};

/** Moves item `index` by `delta` (−1 up, +1 down); the same array when it cannot. */
export const moved = <T>(list: T[], index: number, delta: number): T[] => {
  const to = index + delta;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);

  return next;
};
